import { http } from '../http';
import { DivergenciaSga } from '../sga-divergencia/sgaDivergenciaApi';

/**
 * Reconciliação on-access de um registro local com o SGA (at-32).
 *
 * ⚠️ Estas funções **nunca lançam**. A reconciliação é um extra em cima do
 * fluxo: se o SGA estiver fora do ar (ou o registro for de outra carteira), a
 * tela precisa continuar funcionando exatamente como antes — princípio de
 * resiliência (o SAAC é contingência do SGA, não pode depender dele em tempo
 * real). Falha vira `verificado: false` e nenhuma divergência, e quem chama
 * simplesmente não exibe aviso nenhum.
 */

/**
 * O que aconteceu com UM veículo pendente do associado (at-78, bug B23).
 *
 * 🔴 Existe porque o botão do associado também tenta vincular os veículos sem
 * código no SGA (at-76) e, ao não achar, retornava em silêncio — o usuário lia
 * *"nenhuma diferença"* com o veículo seguindo pendente.
 *
 * ⚠️ A `mensagem` vem **pronta do backend** (convenção at-24/at-32/at-47).
 */
export interface DesfechoVeiculoPendente {
  placa: string;
  situacao: 'VINCULADO' | 'NAO_ENCONTRADO' | 'DONO_DIFERENTE' | 'NAO_TENTADO';
  codigoVeiculo?: string | null;
  /** Placa com que o veículo está no SGA, quando difere da local. */
  placaNoSga?: string | null;
  mensagem: string;
}

export interface ResultadoReconciliacao {
  /** false = não deu para conferir agora (SGA indisponível). Não é erro do fluxo. */
  verificado: boolean;
  motivo: string | null;
  divergencias: DivergenciaSga[];
  /**
   * at-78: só na reconciliação de ASSOCIADO, e só quando há veículo sem código no
   * SGA. **Opcional** — campo aditivo, e as chamadas de veículo não o trazem.
   */
  veiculosPendentes?: DesfechoVeiculoPendente[];
}

const SEM_VERIFICACAO: ResultadoReconciliacao = {
  verificado: false,
  motivo: null,
  divergencias: [],
};

/** Confere um veículo local contra o SGA. Dispare e siga — não bloqueie a tela. */
export const reconciliarVeiculo = async (carroId: string): Promise<ResultadoReconciliacao> => {
  if (!carroId) return SEM_VERIFICACAO;

  try {
    const { data } = await http.post<ResultadoReconciliacao>(
      `/sga-reconciliacao/veiculo/${carroId}`,
    );
    return data ?? SEM_VERIFICACAO;
  } catch {
    // Silencioso de propósito: nada aqui é pré-requisito do fluxo do consultor.
    return SEM_VERIFICACAO;
  }
};

/** Confere um associado local contra o SGA. Mesmas garantias do de veículo. */
export const reconciliarAssociado = async (
  associadoId: string,
): Promise<ResultadoReconciliacao> => {
  if (!associadoId) return SEM_VERIFICACAO;

  try {
    const { data } = await http.post<ResultadoReconciliacao>(
      `/sga-reconciliacao/associado/${associadoId}`,
    );
    return data ?? SEM_VERIFICACAO;
  } catch {
    return SEM_VERIFICACAO;
  }
};

// ---------------------------------------------------------------------------
// Sincronização dirigida pelo usuário (at-32) — os dois sentidos do espelho.
//
// ⚠️ Ao contrário das funções de reconciliação acima, estas **lançam**: são
// ESCRITAS pedidas explicitamente pelo usuário, e falhar em silêncio faria a
// tela mentir que aplicou. Quem chama trata o erro e avisa.
// ---------------------------------------------------------------------------

export interface ResultadoSincronizacao {
  /** Campos que passaram a ter o valor do SGA. */
  aplicados: { campo: string; rotulo: string; valorSga: string | null }[];
  /** Estruturais (troca de titular, carteira…) — seguem na fila do TI. */
  pendentes: { campo: string; rotulo: string; orientacao: string }[];
}

/** SGA → local: aplica no veículo os valores do SGA. */
export const trazerVeiculoDoSga = async (carroId: string): Promise<ResultadoSincronizacao> => {
  const { data } = await http.post<ResultadoSincronizacao>(
    `/sga-reconciliacao/veiculo/${carroId}/trazer-do-sga`,
  );
  return data;
};

/** SGA → local: aplica no associado os valores do SGA. */
export const trazerAssociadoDoSga = async (
  associadoId: string,
): Promise<ResultadoSincronizacao> => {
  const { data } = await http.post<ResultadoSincronizacao>(
    `/sga-reconciliacao/associado/${associadoId}/trazer-do-sga`,
  );
  return data;
};

/**
 * Local → SGA: empurra o cadastro local do veículo (`alterar/veiculo`).
 *
 * Só existe para VEÍCULO: a API do SGA não expõe alteração cadastral de
 * associado (inventário de 31/08/2026) — por isso a faixa não oferece este
 * sentido em telas de associado.
 */
export interface ResultadoEnvioAoSga {
  mensagem: string;
  /** Rótulos das divergências fechadas — texto para o usuário. */
  fechadas: string[];
  /**
   * Ids das mesmas divergências (at-47). É com eles que a tela tira as linhas da
   * faixa **sem reconciliar** — ver `utils/sincronizacaoSga.ts`.
   */
  idsFechados: string[];
  /**
   * Rótulos que **continuam abertos** porque o valor local é vazio/zero e o SGA
   * ignora campo assim (at-47, bug B9). Fechá-los seria afirmar uma resolução que
   * não aconteceu do lado de lá.
   */
  naoAceitosPeloSga: string[];
}

export const enviarVeiculoAoSga = async (carroId: string): Promise<ResultadoEnvioAoSga> => {
  const { data } = await http.post<ResultadoEnvioAoSga>(
    `/sga-reconciliacao/veiculo/${carroId}/enviar-ao-sga`,
  );
  return data;
};
